import React, { useEffect, useRef, useState } from 'react'
import { Stack, Typography, Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { T, useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { DefaultButton } from 'Components/Elements/Forms'
import ShowError from './ShowError'
import SendNowThreeDS from './SendNowThreeDS'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'
import { runDdcHtml, readDirectSessionId, readPrepare, injectCardSecrets, buildCreateOrderResult, isCreateSuccess, readCreateOrderId, readChallengeHtml } from 'Services/FeelGreat/sendNowHelpers'
import { PaymentAPIs } from 'GlobalStores/Checkout/PaymentServices/PaymentAPI'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import { ReactComponent as SuccessIcon } from '../Assets/successv2.svg'
import { get } from 'lodash'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import { getOSName } from 'Helpers/Bowser'
import storeFeelGreat from 'GlobalStores/FeelGreat/StoreFeelGreat'

const SendNow = observer(props => {
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState([])
    const [value, setValue] = useState('1')
    const [isSuccess, setIsSuccess] = useState(false)
    const [cvv, setCvv] = useState('')
    const [challengeHTML, setChallengeHTML] = useState(null)
    // Holds the create-call context so the deferred `complete` can run after the challenge resolves.
    const challengeCtxRef = useRef(null)
    // Set once Send Now succeeds. We refetch the list only when the dialog closes (unmount),
    // never while the success screen is visible — refetching flips the parent `loading` flag,
    // which unmounts this dialog and hides the success screen before the user sees it.
    const succeededRef = useRef(false)

    useEffect(() => {
        return () => {
            if (succeededRef.current && typeof props.handleGetOrder === 'function') {
                props.handleGetOrder()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const order = props.products

    const translate = useTranslate()

    // The FE-driven 3DS Send Now flow (DDC + CVV + Hydra create) is JP-only (USHOP-2821).
    // Every other market keeps the legacy BE-created order flow with no CVV input.
    const isJPN = storeCountry.Country3() === 'JPN'

    // JP can switch between the 3DS flow (runAll) and the legacy MOTO flow (handleSubmit)
    // via the remote subscription setting `sendNow.use3DS`. If the flag is absent (BE hasn't
    // set it yet), fall back to MOTO so JP is not stuck on 3DS while it's unstable.
    const useThreeDS = isJPN && storeFeelGreat.GetSubscriptionConfigData('sendNow.use3DS') === true

    const maskedCard =
        get(order, 'transactions.items[0].methodDetails.creditCardNumberMasked') || get(order, 'transactions.items[0].methodDetails.alias') || ''

    const totalPrice = (get(order, 'terms.total') || get(order, 'lines.items[0].terms.priceEach') || 0).toString()

    const buildSendNowBody = () => ({
        total_price: totalPrice,
        isSkipSubscriptions: value === '1' ? false : true,
        autoship_data: {
            ...order,
            source: {
                agent: 'uFeelGreat',
                medium: 'Internet',
                platform: getOSName()
            }
        }
    })

    // Logs the result via complete; on success shows the confirmation + refreshes the list, else surfaces the error.
    const finishWithComplete = async (createOrder, body, token) => {
        const completeRes = await FeelGreatAPI.SendNowComplete({ ...body, create_order: createOrder }, token)
        if (createOrder.status === 'success' && get(completeRes, 'data.success', true) !== false) {
            // Defer the list refetch to dialog close (see succeededRef cleanup) so it
            // doesn't unmount the success screen before the user sees it.
            succeededRef.current = true
            setIsSuccess(true)
        } else {
            setErrorMessage([
                get(createOrder, 'hydra_response_body.error.error_message') ||
                    get(completeRes, 'data.error.error_message') ||
                    get(completeRes, 'data.error.message') ||
                    translate('send_now_failed', { defaultDisplay: 'Something went wrong. Please try again.' })
            ])
        }
    }

    // The 3DS step-up succeeded — finalize with the post-auth order id (fallback to the create response).
    const onChallengeSuccess = async orderId => {
        const { createRes, hydraCreateUrl, hydraBody, reqHeaders, body } = challengeCtxRef.current || {}
        const base = buildCreateOrderResult({ ok: true, response: createRes, hydraCreateUrl, hydraBody, headers: reqHeaders })
        const createOrder = { ...base, order_id: orderId || base.order_id || readCreateOrderId(createRes) }
        setChallengeHTML(null)
        challengeCtxRef.current = null
        try {
            await finishWithComplete(createOrder, body, StoreAuth.GetToken())
        } catch (error) {
            console.error(error)
            setErrorMessage([
                get(error, 'response.data.error.error_message') ||
                    get(error, 'response.data.message') ||
                    get(error, 'message') ||
                    translate('send_now_failed', { defaultDisplay: 'Something went wrong. Please try again.' })
            ])
        } finally {
            setLoading(false)
        }
    }

    // The 3DS step-up failed — still call complete to log the failure, then show the error.
    const onChallengeFailure = async message => {
        const { createRes, hydraCreateUrl, hydraBody, reqHeaders, body } = challengeCtxRef.current || {}
        const createOrder = buildCreateOrderResult({ ok: false, response: createRes, hydraCreateUrl, hydraBody, headers: reqHeaders })
        setChallengeHTML(null)
        challengeCtxRef.current = null
        try {
            await FeelGreatAPI.SendNowComplete({ ...body, create_order: createOrder }, StoreAuth.GetToken())
        } catch (error) {
            console.error(error)
        }
        setErrorMessage([message || translate('send_now_failed', { defaultDisplay: 'Something went wrong. Please try again.' })])
        setLoading(false)
    }

    // User dismissed the challenge — cancel without logging, allow a retry.
    const onChallengeClose = () => {
        setChallengeHTML(null)
        challengeCtxRef.current = null
        setLoading(false)
    }

    // Legacy non-JP Send Now: BE creates the order (no CVV / no FE 3DS). Restored for all markets except JP.
    const handleSubmit = async () => {
        setLoading(true)
        setErrorMessage([])
        try {
            let resHref = await FeelGreatAPI.SendNowAutoOrder(order.href, StoreAuth.GetToken())

            if (resHref.data) {
                const newData = {
                    total_price: get(props.products, 'terms.total', 0).toString(),
                    isSkipSubscriptions: value === '1' ? false : true,
                    autoship_data: {
                        creator: order.creator,
                        currency: order.currency,
                        customer: order.customer,
                        dateCreated: order.dateCreated,
                        id: order.id,
                        isStaged: order.isStaged,
                        lines: order.lines,
                        market: order.market,
                        modificationDate: order.modificationDate,
                        recurrence: order.recurrence,
                        shipToAddress: order.shipToAddress,
                        shipToEmail: order.shipToEmail,
                        shipToName: order.shipToName,
                        shipToPhone: order.shipToPhone,
                        shippingMethod: resHref.data.shippingMethod,
                        status: order.status,
                        terms: order.terms,
                        transactions: order.transactions,
                        href: order.href,
                        source: {
                            agent: 'uFeelGreat',
                            medium: 'Internet',
                            platform: getOSName()
                        }
                    }
                }

                const res = await FeelGreatAPI.SendNowSubscriptions(newData, StoreAuth.GetToken())

                if (res.data.success) {
                    // Show the success screen now; the list refetch is deferred to dialog
                    // close (see succeededRef cleanup) so it doesn't unmount this screen.
                    succeededRef.current = true
                    setIsSuccess(true)
                } else {
                    throw new Error(res.data.error.error_message)
                }
            }
        } catch (error) {
            console.error(error)
            if (error.response.status === 402) {
                setErrorMessage([error.response.data.error.error_message])
            } else if (error.response.status === 504) {
                setErrorMessage([error.response.data.message])
            } else {
                if (error.response.data.error?.error_message) {
                    setErrorMessage([error.response.data.error?.error_message])
                } else {
                    setErrorMessage([error.response.data.error.message])
                }
            }
        } finally {
            setLoading(false)
        }
    }

    // FE-driven Send Now 3DS flow (USHOP-2821): DDC → prepare → inject secrets → create on Hydra → complete.
    // If the create response carries a 3DS step-up (challengeHTML), the challenge modal runs before complete.
    const runAll = async () => {
        setLoading(true)
        setErrorMessage([])
        try {
            const token = StoreAuth.GetToken()
            const body = buildSendNowBody()

            // Guard: CVV is required to fill {{replaceCreditCardSecurityCode}} (3–4 digits, matching checkout).
            if (cvv.length < 3) {
                setErrorMessage([translate('cvv_required', { defaultDisplay: 'Please enter the card security code (CVV).' })])
                return
            }

            // 0) DDC — collect the sessionId needed to fill {{replaceSessionId}}.
            const ddcRes = await PaymentAPIs.RequestWorldPayDDC(maskedCard)
            let sessionId = readDirectSessionId(ddcRes)
            if (!sessionId) {
                sessionId = await runDdcHtml(get(ddcRes, 'data.ddcHTML', ''))
            }
            if (!sessionId) {
                setErrorMessage([translate('ddc_failed', { defaultDisplay: 'Could not verify your card. Please try again.' })])
                return
            }

            // 1) Prepare — returns hydra_body (with placeholders) + hydra_create_url.
            const prepareRes = await FeelGreatAPI.SendNowPrepare(body, token)
            const { hydraBody, hydraCreateUrl } = readPrepare(prepareRes)
            if (!hydraBody || !hydraCreateUrl) {
                const prepareErr =
                    get(prepareRes, 'data.error.error_message') ||
                    get(prepareRes, 'data.error.message') ||
                    get(prepareRes, 'data.message') ||
                    translate('send_now_failed', { defaultDisplay: 'Something went wrong. Please try again.' })
                setErrorMessage([prepareErr])
                return
            }

            // 2) Inject the DDC sessionId + CVV into the hydra_body placeholders.
            injectCardSecrets(hydraBody, sessionId, cvv)

            // 3) Create on Hydra.
            const reqHeaders = { 'content-type': 'application/json', authorization: token }
            let createRes
            try {
                createRes = await FeelGreatAPI.SendNowCreateOnHydra(hydraCreateUrl, hydraBody, token)
            } catch (createErr) {
                // The create call itself errored (no order, no challenge possible) — log the fail via complete.
                const createOrder = buildCreateOrderResult({ ok: false, response: createErr.response || {}, hydraCreateUrl, hydraBody, headers: reqHeaders })
                await finishWithComplete(createOrder, body, token)
                return
            }

            // 3b) 3DS step-up required? Render the challenge and defer `complete` until it resolves.
            const challenge = readChallengeHtml(createRes)
            if (challenge) {
                challengeCtxRef.current = { createRes, hydraCreateUrl, hydraBody, reqHeaders, body }
                setChallengeHTML(challenge)
                return // keep loading; the challenge handlers run complete and clear loading.
            }

            // 4) Frictionless — finalize immediately.
            const createOrder = buildCreateOrderResult({
                ok: isCreateSuccess(createRes),
                response: createRes,
                hydraCreateUrl,
                hydraBody,
                headers: reqHeaders
            })
            await finishWithComplete(createOrder, body, token)
        } catch (error) {
            console.error(error)
            const status = get(error, 'response.status')
            if (status === 402) {
                setErrorMessage([get(error, 'response.data.error.error_message')])
            } else if (status === 504) {
                setErrorMessage([get(error, 'response.data.message')])
            } else {
                const msg =
                    get(error, 'response.data.error.error_message') ||
                    get(error, 'response.data.error.message') ||
                    get(error, 'message') ||
                    translate('send_now_failed', { defaultDisplay: 'Something went wrong. Please try again.' })
                setErrorMessage([msg])
            }
        } finally {
            // Keep loading while the 3DS challenge modal is open; its handlers clear it.
            if (!challengeCtxRef.current) {
                setLoading(false)
            }
        }
    }

    const handleChange = event => {
        setValue(event.target.value)
    }

    const getDateSuffix = day => {
        if (day >= 11 && day <= 13) {
            return 'th'
        }

        const lastDigit = day % 10

        switch (lastDigit) {
            case 1:
                return 'st'
            case 2:
                return 'nd'
            case 3:
                return 'rd'
            default:
                return 'th'
        }
    }

    const getDate = () => {
        const dayOfMonth = SetConvertDate(`2025-01-${props.dateOfmonth}`)
        return dayOfMonth
    }

    const SetConvertDate = dateToConvert => {
        if (dateToConvert) {
            const Locale = storeCountry.GetLocale(StoreTranslate.CurrentLanguage())
            const date = new Date(dateToConvert)

            let convertedDate
            if (Locale === 'ja-JP') {
                convertedDate = date.toLocaleDateString(Locale, {  day: 'numeric' })

                return convertedDate
            } else {
                const day = date.getDate()
                // get suffix of the day
                const suffix = getDateSuffix(day)
                convertedDate = `${day}${suffix}`
            }

            return convertedDate
        }

        return dateToConvert
    }

    const ShowSuccess = () => {
        return (
            <Stack gap="40px">
                <Typography variant="title2" textAlign="center">
                    <T>fg_confirmed</T>
                </Typography>

                <Stack flexDirection="row" justifyContent="center">
                    <SuccessIcon />
                </Stack>

                <Typography variant="caption5" textAlign="center">
                    {value === '1' ? translate('send_now_option_1').replace('{{date}}', getDate()) : translate('send_now_option_2')}
                </Typography>

                <Stack flexDirection="row" justifyContent="center">
                    <DefaultButton
                        type="button"
                        titleText={translate('close', { defaultDisplay: 'Close' })}
                        width="220px"
                        onClick={() => props.handleClose()}
                        fontSize="20px"
                        fontWeight="400"
                    />
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack gap="30px">
            {isSuccess ? (
                <ShowSuccess />
            ) : (
                <>
                    <Typography variant="title2">
                        <T>send_now_title</T>
                    </Typography>
                    <FormControl>
                        <RadioGroup
                            aria-labelledby="demo-controlled-radio-buttons-group"
                            name="controlled-radio-buttons-group"
                            value={value}
                            onChange={handleChange}
                            sx={{
                                gap: '20px'
                            }}>
                            <FormControlLabel
                                value="1"
                                control={
                                    <Radio
                                        sx={{
                                            color: '#CDDDED',
                                            paddingTop: '0px',

                                            '&.Mui-checked': {
                                                color: '#5a8fc3'
                                            },

                                            '.MuiTouchRipple-root': {
                                                top: '-8px'
                                            },

                                            '& .MuiSvgIcon-root': {
                                                fontSize: 18
                                            }
                                        }}
                                        color="secondary"
                                    />
                                }
                                label={
                                    <Typography variant="caption4" color="#153862">
                                        {translate('send_now_option_1').replace('{{date}}', getDate())}
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="2"
                                control={
                                    <Radio
                                        sx={{
                                            color: '#CDDDED',
                                            paddingTop: '0px',

                                            '&.Mui-checked': {
                                                color: '#5a8fc3'
                                            },

                                            '.MuiTouchRipple-root': {
                                                top: '-8px'
                                            },

                                            '& .MuiSvgIcon-root': {
                                                fontSize: 18
                                            }
                                        }}
                                        color="secondary"
                                    />
                                }
                                label={
                                    <Typography variant="caption4" color="#153862">
                                        {translate('send_now_option_2')}
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    {useThreeDS && (
                        <Stack sx={{ maxWidth: '200px' }}>
                            <label htmlFor="send-now-cvv" style={{ paddingLeft: '16px', paddingBottom: '8px' }}>
                                <Typography variant="body5" fontSize={'14px'} fontWeight={'400'} fontFamily={'Inter, NotoSansThai'} color="#8A9BB0" component={'span'}>
                                    {translate('cvv', { defaultDisplay: 'CVV' })}
                                </Typography>
                            </label>
                            <input
                                id="send-now-cvv"
                                type="password"
                                autoComplete="off"
                                inputMode="numeric"
                                maxLength={4}
                                value={cvv}
                                onChange={e => setCvv(e.target.value.replace(/\D/g, ''))}
                                style={{
                                    border: '1px solid #CCDEEF',
                                    borderRadius: '8px',
                                    padding: '10px 16px',
                                    height: '50px',
                                    outline: 'none',
                                    fontSize: '16px',
                                    fontWeight: '400',
                                    fontFamily: 'Poppins,Inter, NotoSansThai',
                                    color: '#003B6F',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </Stack>
                    )}

                    {errorMessage.length > 0 && <ShowError list={errorMessage} noTranslate={true} />}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                        <Box onClick={() => props.handleClose()}>
                            <Typography variant="link" color="#000000" fontSize="16px">
                                <T>cancel</T>
                            </Typography>
                        </Box>
                        <DefaultButton
                            type="submit"
                            fullWidth={true}
                            titleText={translate('save')}
                            width="220px"
                            onClick={() => (useThreeDS ? runAll() : handleSubmit())}
                            isloading={loading.toString()}
                            fontSize="20px"
                            fontWeight="400"
                            disabled={loading || (useThreeDS && (cvv.length < 3 || !maskedCard))}
                        />
                    </Box>
                </>
            )}

            {challengeHTML && (
                <SendNowThreeDS
                    challengeHTML={challengeHTML}
                    fallbackOrderId={readCreateOrderId(challengeCtxRef.current?.createRes)}
                    onSuccess={onChallengeSuccess}
                    onFailure={onChallengeFailure}
                    onClose={onChallengeClose}
                />
            )}
        </Stack>
    )
})

export default SendNow
